import { createImmutableProxy, EventEmitter, generateUUID } from '@sm/max-common';
import { IS_PREVIEW, MaxPageRender, Logger, _ROUTE_STACKS } from '../share';
import { RouterChangeData, RouterStack } from '../types';
import { addStartSlash, getSearch2obj, transform2PureRoute } from '../utils';

/**
 * 路由历史记录类
 *
 * @description
 * 用于管理路由变更，包括路由栈缓存、路由变更记录缓存、触发页面渲染等
 *
 * 为什么这里要维护一个路由栈缓存_routeStacks？
 * web/h5中，路由变更历史及状态其实浏览器的window.history都有维护，但仍然要维护一份路由栈缓存_routeStacks的核心原因是：
 * 1. navigateBackTo -> backTo(url, params) 需要调用 history.go(-num) 回退到指定的路由，回退长度 num 需要根据 url 与 路由栈计算出来，但是浏览器没有提供原生获取路由栈的API。
 *    所以只有借助外部单独的路由栈缓存_routeStacks来计算。
 *
 * 相同点：
 * 预览环境和产物环境都消费_routeStacks路由栈缓存。
 * 差异点：
 * 1. 预览环境下，不处理浏览器的前进与后退，所以也就无需维护浏览器的路由堆栈。
 * 2. 产物环境下，要处理浏览器的前进与后退，所以浏览器的路由堆栈要同步更新。
 */
export class MaxRouterHistory {
  // 路由栈缓存，用于backTo索引回退长度
  private get _routeStacks(): RouterStack[] {
    return JSON.parse(sessionStorage.getItem(_ROUTE_STACKS)) ?? [];
  }
  private set _routeStacks(v) {
    sessionStorage.setItem(_ROUTE_STACKS, JSON.stringify(v));
  }
  // 页面渲染器
  public maxPageRender: MaxPageRender;

  public onRouteChangeEmitter = new EventEmitter<RouterChangeData>();

  constructor() {
    this.maxPageRender = new MaxPageRender();

    window.addEventListener('popstate', (event) => {
      // 预览中不处理
      if (IS_PREVIEW) {
        return;
      }
      if (event.state?.id) {
        this.emitRouteChange(() => {
          // 渲染目标路由
          this.renderRouteByTargetStack(event.state);
        });
      }
    });
  }

  /**
   * 路由变更事件触发函数，用于统一处理路由变化时的事件派发和数据传递
   * 此函数封装了路由变化的完整生命周期处理，包括获取前后路由状态、执行变更操作、触发事件通知等
   *
   * @param {Function} callback - 路由变更的实际执行回调函数，包含具体的路由跳转逻辑
   *
   * @description
   * 函数执行流程:
   * 1. 获取路由变更前的当前路由状态，并进行深度拷贝以保存旧状态
   * 2. 执行传入的回调函数，完成实际的路由变更操作
   * 3. 创建路由数据代理对象，包含变更前后的路由状态信息
   * 4. 通过事件发射器触发路由变化事件，通知所有注册的监听器
   * 5. 记录路由操作的调试日志
   *
   * @remarks
   * - 使用Object.assign创建上一个路由的深拷贝，避免引用关系导致的状态污染
   * - 通过createImmutableProxy创建不可变数据代理，确保路由数据不会被外部直接修改
   * - 采用事件驱动架构，实现了路由变化的解耦通知机制
   * - 内置日志记录功能，便于开发调试和问题追踪
   */
  public emitRouteChange(callback: () => void) {
    // 获取上一个路由信息：从路由历史栈中取出最后一个路由
    // 如果路由栈为空，则使用空对象作为默认值
    const lastRoute = Object.assign({}, this.getCurrentRouteStack());

    // 执行路由变更的回调函数
    callback();

    const currentRoute = this.getCurrentRouteStack();
    // 向外暴露路由变化数据，使用Proxy代理对象，确保数据不可直接修改
    const routeDataProxy = createImmutableProxy({
      currentRoute: {
        url: currentRoute.url,
        params: currentRoute.params,
        search: currentRoute.search,
      },
      lastRoute: {
        url: lastRoute.url,
        params: lastRoute.params,
        search: lastRoute.search,
      }, // 上一个路由信息
    });
    // 触发路由变化事件：通知所有监听器路由已发生变化
    this.onRouteChangeEmitter.emit(routeDataProxy);
    // 打印调试日志
    Logger.log('routeChange', routeDataProxy);
  }

  private renderRouteByTargetStack(targetStack: RouterStack) {
    const routeStack = this._routeStacks;
    // 触发页面渲染
    this.maxPageRender.render(targetStack.url);

    // 找到目标回退路由的索引
    const targetIndex = routeStack.findIndex((item) => item.id === targetStack.id);
    // 前进场景
    if (targetIndex === -1) {
      // 前进场景下，新增一条路由缓存
      routeStack.push({ ...targetStack });
    }
    // 后退场景
    else {
      // 后退场景下，删除指定长度的路由缓存
      routeStack.splice(targetIndex + 1);
      // 后退场景业务场景有可能更新路由参数，所以需要更新history
      this._replaceState(routeStack[routeStack.length - 1]);
    }
    this._routeStacks = routeStack;
  }

  private _pushState(stack: RouterStack) {
    // 预览中不处理
    if (IS_PREVIEW) {
      return;
    }
    // 新增路由
    history.pushState(stack, '', addStartSlash(stack.url));
  }
  private _replaceState(stack: RouterStack) {
    // 预览中不处理
    if (IS_PREVIEW) {
      return;
    }
    // 替换路由
    history.replaceState(stack, '', addStartSlash(stack.url));
  }

  public go(url: string, params?: any) {
    const routeStack = this._routeStacks;
    this.emitRouteChange(() => {
      // 渲染页面
      this.maxPageRender.render(url);
      // 跳转不同路由时才更新路由栈，比如：浏览器刷新时不更新
      if (!routeStack.length || (routeStack.length && routeStack[routeStack.length - 1].url !== url)) {
        const stack = {
          id: generateUUID(),
          url,
          pureRoute: transform2PureRoute(url),
          params,
          search: getSearch2obj(url),
        };
        // 新增一条路由
        this._pushState(stack);
        // 新增一条路由缓存
        routeStack.push(stack);
        this._routeStacks = routeStack;
      }
    });
  }

  public back(num: number, params?: any) {
    const routeStack = this._routeStacks;
    // 回退长度大于路由栈长度，报错
    if (num > routeStack.length - 1) {
      return new Error(`back route length ${num} is greater than route stacks length ${routeStack.length}`);
    }
    // 计算回退后的目标路由
    const targetRouteStack = routeStack[routeStack.length - 1 - num];
    // 更新路由栈中的params参数
    targetRouteStack.params = params;
    this._routeStacks = routeStack;

    if (IS_PREVIEW) {
      this.emitRouteChange(() => {
        this.renderRouteByTargetStack(targetRouteStack);
      });
    } else {
      history.go(-num);
    }
  }

  public backTo(url: string, params?: any) {
    const routeStack = this._routeStacks;
    // 先将路由栈反转，index是要跳转的路由在路由栈中的索引，所以需要后退的路由长度也恰好等于index，不需要额外加 1
    const num = [...routeStack].reverse().findIndex((routeStack) => routeStack.pureRoute === transform2PureRoute(url));
    if (num === -1) {
      Logger.error(`backTo url ${url} not found`);
      return;
    }
    // 计算回退后的目标路由
    const targetRouteStack = routeStack[routeStack.length - 1 - num];
    // 更新最后一个路由的url
    targetRouteStack.url = url;
    // 更新最后一个路由的参数
    targetRouteStack.params = params;
    // 更新路由栈中的search参数
    targetRouteStack.search = getSearch2obj(url);
    this._routeStacks = routeStack;

    if (IS_PREVIEW) {
      this.emitRouteChange(() => {
        // 渲染页面
        this.renderRouteByTargetStack(targetRouteStack);
      });
    } else {
      history.go(-num);
    }
  }

  public redirect(url: string, params?: any) {
    const routeStack = this._routeStacks;
    this.emitRouteChange(() => {
      // 渲染页面
      this.maxPageRender.render(url, true);
      const stack = {
        id: generateUUID(),
        url,
        pureRoute: transform2PureRoute(url),
        params,
        search: getSearch2obj(url),
      };
      // 替换路由缓存
      routeStack.splice(routeStack.length - 1, 1, stack);
      this._routeStacks = routeStack;
      // 替换路由
      this._replaceState(stack);
    });
  }

  public clear() {
    this._routeStacks = [];
  }

  /**
   * 获取路由堆栈信息，包含search参数
   * @returns 返回内部存储的路由堆栈数组
   */
  public getRouteStacks() {
    return [...this._routeStacks];
  }

  public getCurrentRouteStack() {
    return this._routeStacks[this._routeStacks.length - 1];
  }
}
